import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import {
  getSidebarItems,
  groupItems,
  GROUP_TITLES,
  GROUP_TITLES_EN,
  GROUP_ICONS,
  GROUP_ORDER,
  COLLAPSIBLE_GROUPS,
  isItemEnabled,
  getLabelForLang,
} from '../navigation/sidebarConfig';
import { ICONS as NAV_ICONS } from './navigation/icons';
import { useProfile } from '../contexts/ProfileContext';
import { usePlanCapabilities } from '../hooks/usePlanCapabilities';
import SportSelector from './navigation/SportSelector';
import RoleSwitcher from './navigation/RoleSwitcher';
import { useSport } from '../contexts/SportContext';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';
import { useGovernanceLang } from '../contexts/GovernanceLangContext';
import { FEATURES } from '../config/features';


// *** Cores da marca ***

// Admin emails (same as GovernanceGuard)
const ICONS = NAV_ICONS; // alias for institutional footer links
const ADMIN_EMAILS = (process.env.REACT_APP_GOVERNANCE_ADMIN_EMAILS || '').split(',').filter(Boolean);

const MobileMenuButton = ({ isOpen = false, onClose = () => {}, onToggle = () => {} }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { activeRole, roles: userRoles } = useRBAC();

  // Check if user is admin (for governance section visibility)
  const isAdmin = user?.role === 'super_admin' || (user?.email && ADMIN_EMAILS.includes(user.email));

  const { activeSport } = useSport();
  const { isSuperAdmin, superMode, hasCapability } = useProfile();

  // Lang context — same unified toggle as GlobalSidebar
  const { lang: sportsLang, setLang: setSportsLang } = useSportsLang();
  const { setLang: setGovLang } = useGovernanceLang();
  const lang = sportsLang;
  const setLang = (l) => { setSportsLang(l); setGovLang(l); };

  // Selectors (sport, profile) are admin-only tools.
  const showSelectorsSection = isAdmin;

  const [collapsedSections, setCollapsedSections] = useState({});
  const [selectorsHidden, setSelectorsHidden] = useState(() => {
    try { return localStorage.getItem('arenastats_selectors_hidden') === 'true'; } catch { return false; }
  });
  const toggleSelectors = () => {
    setSelectorsHidden(prev => {
      const next = !prev;
      try { localStorage.setItem('arenastats_selectors_hidden', String(next)); } catch {}
      return next;
    });
  };

  const FEATURE_FLAGS = FEATURES;

  // Use same getSidebarItems as GlobalSidebar — ensures lang-aware labels
  const { hasPlanCapability } = usePlanCapabilities();
  const sidebarItems = getSidebarItems({
    role: activeRole,
    userRoles,
    sport: activeSport,
    flags: FEATURE_FLAGS,
    isAdmin,
    superMode,
    hasCapability,
    hasPlanCapability, // P3.2: capabilities do PLANO (GET /me/context)
  });
  const groupedItems = groupItems(sidebarItems);

  // Scroll Lock quando aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // Fechar drawer ao navegar (só depende do pathname — isOpen/onClose lidos
  // no momento do disparo, não como dependências, para evitar fechar
  // imediatamente ao abrir)
  useEffect(() => {
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Menu Icon Component
  const MenuIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );

  // Close Icon Component
  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  // Drawer usando React Portal
  const drawer = isOpen && createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black transition-opacity duration-300 z-[200]"
        style={{
          opacity: isOpen ? 0.5 : 0,
          pointerEvents: isOpen ? 'auto' : 'none'
        }}
        onClick={onClose}
      />

      {/* Drawer Container */}
      <div 
        className={`
          fixed top-0 right-0 h-screen w-80 max-w-full
          bg-white shadow-2xl
          flex flex-col overflow-hidden
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          z-[201]
        `}
      >
        {/* Header com Close Button */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img 
              src="/vukz-logo.jpeg" 
              alt="VUKZ" 
              style={{ height: '56px' }}
              className="w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'rounded-xl flex items-center justify-center font-black text-white text-lg';
                placeholder.style.cssText = 'width:56px;height:56px;background:#111111';
                placeholder.innerHTML = 'V';
                e.target.parentNode.appendChild(placeholder);
              }}
            />
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            style={{ color: BRAND_GREEN }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Conteúdo com Scroll */}
        <div className="flex-1 overflow-y-auto overscroll-contain">

          {/* Seletores (colapsável) — hidden for single-role/single-sport non-admin */}
          {showSelectorsSection && (
          <div className="border-b border-gray-100">
            <button
              onClick={toggleSelectors}
              className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors duration-200"
            >
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: BRAND_GOLD }}>
                {selectorsHidden
                ? (lang === 'en' ? 'Show selectors' : 'Mostrar seletores')
                : (lang === 'en' ? 'Hide selectors' : 'Ocultar seletores')}
              </span>
              <svg
                className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${selectorsHidden ? '' : 'rotate-180'}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!selectorsHidden && (
              <div>
                <SportSelector />
                <RoleSwitcher />
              </div>
            )}
          </div>
          )}

          {/* NAVEGACAO DINAMICA */}
          {/* NAVEGACAO DINAMICA — sidebarConfig (same as GlobalSidebar) */}
          {GROUP_ORDER.map((groupKey, groupIdx) => {
            const items = groupedItems[groupKey] || [];
            if (items.length === 0) return null;

            const hasVisibleItems = isSuperAdmin
              ? true
              : items.some(item => isItemEnabled(item, FEATURE_FLAGS, isSuperAdmin, hasCapability));
            if (!hasVisibleItems) return null;

            const isCollapsible = COLLAPSIBLE_GROUPS.includes(groupKey);
            const isCollapsed = isCollapsible && !!collapsedSections[groupKey];
            const groupTitle = (lang === 'en' ? GROUP_TITLES_EN[groupKey] : GROUP_TITLES[groupKey]) || groupKey;
            const groupIconName = GROUP_ICONS[groupKey];
            const GroupIconComponent = groupIconName ? NAV_ICONS[groupIconName] : null;
            const hasActiveChild = items.some(item => item.route && location.pathname.startsWith(item.route.split('?')[0]));

            return (
            <div
              key={groupKey}
              className={`px-3 pt-4 pb-2 ${groupIdx > 0 ? 'border-t border-gray-100' : ''}`}
            >
              {groupTitle && isCollapsible ? (
                <button
                  onClick={() => setCollapsedSections(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                    hasActiveChild ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  {GroupIconComponent && <div style={{ color: BRAND_GREEN }}><GroupIconComponent /></div>}
                  <span className="text-sm font-bold" style={{ color: BRAND_GREEN }}>{groupTitle}</span>
                  <svg className={`w-3.5 h-3.5 ml-auto text-gray-400 transition-transform duration-200 ${!isCollapsed ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              ) : groupTitle ? (
                <h4 className="px-3 text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_GREEN, opacity: 0.6 }}>
                  {groupTitle}
                </h4>
              ) : null}

              {(!isCollapsible || !isCollapsed) && (
              <nav className={`space-y-1 ${isCollapsible ? 'mt-1 ml-2 pl-2 border-l-2 border-gray-100' : ''}`}>
                {items.map((item) => {
                  const enabled = isItemEnabled(item, FEATURE_FLAGS, isSuperAdmin, hasCapability);
                  const isActive = item.route && location.pathname === item.route.split('?')[0];
                  const IconComponent = item.iconKey ? NAV_ICONS[item.iconKey] : null;

                  if (!enabled) {
                    if (!isSuperAdmin) return null;
                    return (
                      <div key={item.id}
                        className="relative flex items-center gap-3 px-3 py-2.5 rounded-lg opacity-50 cursor-not-allowed"
                        title={lang === 'en' ? 'Soon' : 'Em breve'}
                      >
                        {IconComponent && <div style={{ color: BRAND_GREEN }} className="opacity-50"><IconComponent /></div>}
                        <span className="text-sm" style={{ color: BRAND_GREEN }}>{getLabelForLang(item, lang)}</span>
                        <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-300 text-gray-600">
                          {lang === 'en' ? 'Soon' : 'Em breve'}
                        </span>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={item.route}
                      onClick={onClose}
                      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                        isActive ? 'bg-gray-100 font-bold' : 'hover:bg-gray-50 active:bg-gray-100'
                      }`}
                    >
                      {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r-sm" style={{ backgroundColor: BRAND_GREEN }} />}
                      {IconComponent && (
                        <div style={{ color: BRAND_GREEN }} className={`${isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}>
                          <IconComponent />
                        </div>
                      )}
                      <span className="text-sm" style={{ color: BRAND_GREEN }}>
                        {getLabelForLang(item, lang)}
                      </span>
                      {item.badge && item.badge !== 'Em breve' && item.badge !== 'Soon' && (
                        <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          item.badge === 'NEW' ? 'bg-green-500 text-white' :
                          item.badge === 'BETA' ? 'bg-blue-500 text-white' :
                          item.badge === 'AI' ? 'bg-purple-500 text-white' :
                          item.badge === 'FOUNDER' ? 'text-white' : 'bg-gray-200 text-gray-700'
                        }`} style={item.badge === 'FOUNDER' ? { backgroundColor: BRAND_GOLD } : {}}>
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
              )}
            </div>
            );
          })}

          {/* RODAPÉ INSTITUCIONAL - 100% IDÊNTICO ao GlobalSidebar */}
          <div className="mt-4 px-4 pb-6 pt-4 border-t border-gray-100">
            {/* LANG TOGGLE */}
            <div className="flex items-center justify-between mb-4 px-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Language</span>
              <div className="flex rounded-lg overflow-hidden border border-gray-200">
                <button
                  onClick={() => setLang('pt')}
                  className="px-2.5 py-1 text-xs font-bold transition-colors"
                  style={lang === 'pt'
                    ? { backgroundColor: BRAND_GREEN, color: '#fff' }
                    : { backgroundColor: 'transparent', color: '#9ca3af' }}
                >
                  PT
                </button>
                <button
                  onClick={() => setLang('en')}
                  className="px-2.5 py-1 text-xs font-bold transition-colors"
                  style={lang === 'en'
                    ? { backgroundColor: BRAND_GREEN, color: '#fff' }
                    : { backgroundColor: 'transparent', color: '#9ca3af' }}
                >
                  EN
                </button>
              </div>
            </div>
            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">
              {lang === 'en' ? 'Institutional Links' : 'Links Institucionais'}
            </h4>
            <div className="space-y-2">
              <Link to="/sobre" onClick={onClose} className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.info && <ICONS.info />} {lang === 'en' ? 'About' : 'Sobre'}
              </Link>
              <Link to="/privacidade" onClick={onClose} className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.lock && <ICONS.lock />} {lang === 'en' ? 'Privacy' : 'Privacidade'}
              </Link>
              <Link to="/termos" onClick={onClose} className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.fileText && <ICONS.fileText />} {lang === 'en' ? 'Terms of Use' : 'Termos de Uso'}
              </Link>
              <Link to="/contato" onClick={onClose} className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.mail && <ICONS.mail />} {lang === 'en' ? 'Support & Contact' : 'Suporte & Contato'}
              </Link>
            </div>

            {/* Microsoft for Startups */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{lang === 'en' ? 'Built with support from' : 'Construído com apoio de'}</p>
              <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <img 
                  src="/microsoft-logo.png" 
                  alt="Microsoft for Startups" 
                  className="h-5 w-auto object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <div>
                  <p className="text-[11px] font-bold text-gray-700">Microsoft for Startups</p>
                  <p className="text-[9px] text-gray-400">Empowering Innovation</p>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-6 flex items-center gap-2 opacity-50 grayscale hover:grayscale-0 transition-all">
              <img 
                src="/vukz-logo.jpeg" 
                alt="VUKZ" 
                className="h-5"
                onError={(e) => e.target.style.display = 'none'}
              />
              <span className="text-[10px] text-gray-400 font-medium">© 2026 VUKZ</span>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <>
      {/* Botão Hamburguer (apenas em Mobile) */}
      <button 
        className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
        style={{ color: BRAND_GREEN }}
        onClick={onToggle}
        aria-label="Menu"
      >
        <MenuIcon />
      </button>

      {/* Drawer Portal */}
      {drawer}
    </>
  );
};

export default MobileMenuButton;
